import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bankingAPI, accountsAPI, companyAPI } from '../../services/api';
import toast from 'react-hot-toast';
import '../../styles/data-form.css';
import { fmtMoney as fmt } from '../../utils/currency';

/** Format a value in an explicit currency code (falls back to a plain label). */
function fmtCur(value, code) {
  const n = Number(value) || 0;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(n);
  } catch (e) {
    return `${code} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

/**
 * Record Deposit — add money to a bank account.
 * Debits the bank's GL account and credits the selected source accounts,
 * writes a bank register transaction, and updates the bank balance.
 *
 * Amounts can be entered in any configured currency (e.g. USD). They are
 * automatically converted to the company base currency (e.g. AFN) using the
 * daily exchange rate and booked in the base currency; the bank register and
 * bank balance are kept in the bank account's own currency.
 */
export default function RecordDeposit() {
  const navigate = useNavigate();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [rates, setRates] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    bank_account_id: '',
    deposit_date: today,
    deposit_slip_number: '',
    memo: '',
  });
  const [currency, setCurrency] = useState('');
  const [exchangeRate, setExchangeRate] = useState(1);
  const [items, setItems] = useState([
    { source_account_id: '', description: '', amount: '' },
  ]);

  useEffect(() => {
    bankingAPI.accounts().then(setBankAccounts).catch(e => toast.error(e.message));
    accountsAPI.list().then(setGlAccounts).catch(() => {});
    Promise.all([
      companyAPI.current().catch(() => null),
      companyAPI.currencies().catch(() => []),
      companyAPI.exchangeRates().catch(() => []),
    ]).then(([company, currs, rateRows]) => {
      const base = company?.base_currency || 'USD';
      setBaseCurrency(base);
      setCurrencies(currs.length ? currs : [{ code: 'USD', name: 'US Dollar', symbol: '$' }]);
      setRates(rateRows || []);
      setCurrency(base);
      setExchangeRate(1);
    });
  }, []);

  // Auto-fill the exchange rate from the saved rate when the entry currency changes
  useEffect(() => {
    if (!currency || currency === baseCurrency) { setExchangeRate(1); return; }
    const latest = rates.find(r => r.from_currency === currency && r.to_currency === baseCurrency);
    setExchangeRate(latest ? parseFloat(latest.rate) : 1);
  }, [currency, baseCurrency, rates]);

  const u = (f) => (e) => setForm({ ...form, [f]: e.target.value });
  const setItem = (i, field, value) => {
    setItems(items.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };
  const addItem = () => setItems([...items, { source_account_id: '', description: '', amount: '' }]);
  const removeItem = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);

  const selectBank = (e) => {
    const id = e.target.value;
    setForm({ ...form, bank_account_id: id });
    const bank = bankAccounts.find(b => String(b.id) === String(id));
    if (bank) setCurrency(bank.currency || baseCurrency);
  };

  const isForeign = !!currency && currency !== baseCurrency;
  const fxRate = parseFloat(exchangeRate) || 0;
  const toBase = (v) => isForeign ? v * fxRate : v;
  const totalEntered = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const total = items.reduce((s, it) => s + toBase(parseFloat(it.amount) || 0), 0);
  const selectedBank = bankAccounts.find(b => String(b.id) === String(form.bank_account_id));
  const bankCur = selectedBank && selectedBank.currency ? String(selectedBank.currency).toUpperCase() : baseCurrency;
  const bankFx = bankCur === baseCurrency ? 1
    : (bankCur === (currency || baseCurrency) ? fxRate
      : (() => {
          const r = rates.find(x => String(x.from_currency).toUpperCase() === bankCur && String(x.to_currency).toUpperCase() === baseCurrency);
          return r ? parseFloat(r.rate) : null;
        })());
  const bankAmount = bankFx ? Math.round((total / bankFx) * 100) / 100 : total;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bank_account_id) { toast.error('Select a bank account'); return; }
    if (total <= 0) { toast.error('Enter at least one deposit line with an amount'); return; }
    if (isForeign && fxRate <= 0) { toast.error(`Set the exchange rate for 1 ${currency} = ? ${baseCurrency}`); return; }
    setLoading(true);
    try {
      await bankingAPI.createDeposit({
        ...form,
        currency: currency || baseCurrency,
        exchange_rate: isForeign ? fxRate : 1,
        total,
        items: items
          .filter(it => parseFloat(it.amount) > 0)
          .map(it => ({
            source_account_id: parseInt(it.source_account_id),
            description: it.description || 'Deposit',
            amount: parseFloat(it.amount),
          })),
      });
      toast.success(`Deposit recorded — new balance ${fmtCur((parseFloat(selectedBank?.bank_balance) || 0) + bankAmount, bankCur)}`);
      navigate('/banking/transactions');
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="toolbar">
        <h2>Record Deposit</h2>
        <div className="toolbar-right">
          <button className="btn btn-outline" onClick={() => navigate('/banking')}>Cancel</button>
          <button className="btn btn-primary" form="deposit-form" disabled={loading}>
            {loading ? 'Saving...' : 'Record Deposit'}
          </button>
        </div>
      </div>

      <div className="df-editor" style={{ maxWidth: 900 }}>
        <div className="df-section-header"><span>Deposit Details</span></div>
        <form id="deposit-form" onSubmit={handleSubmit}>
          <div className="df-body">
            <div className="df-grid cols-2">
              <div className="df-field">
                <label>Bank Account <span className="req">*</span></label>
                <select required value={form.bank_account_id} onChange={selectBank}>
                  <option value="">Select bank account...</option>
                  {bankAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.bank_name || a.account_name} ({fmtCur(a.bank_balance, (a.currency || baseCurrency) || baseCurrency)})</option>
                  ))}
                </select>
              </div>
              <div className="df-field">
                <label>Date <span className="req">*</span></label>
                <input type="date" required value={form.deposit_date} onChange={u('deposit_date')} />
              </div>
              <div className="df-field">
                <label>Currency ({baseCurrency} is base)</label>
                <select value={currency || baseCurrency} onChange={e => setCurrency(e.target.value)}>
                  {currencies.map(c => (
                    <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                  ))}
                </select>
              </div>
              <div className="df-field">
                <label>Exchange Rate <span style={{ fontWeight: 400, color: '#94a3b8' }}>(1 {currency || baseCurrency} = ? {baseCurrency})</span></label>
                {isForeign ? (
                  <input type="number" step="0.0001" min="0.0001" value={exchangeRate || ''}
                    placeholder="e.g. 64.50" onChange={e => setExchangeRate(parseFloat(e.target.value) || 0)} />
                ) : (
                  <input value="1.0000" disabled />
                )}
              </div>
              <div className="df-field">
                <label>Deposit Slip Number</label>
                <input value={form.deposit_slip_number} onChange={u('deposit_slip_number')} placeholder="Optional" />
              </div>
              <div className="df-field">
                <label>Memo</label>
                <input value={form.memo} onChange={u('memo')} placeholder="Optional memo" />
              </div>
            </div>

            <div className="df-section-header" style={{ marginTop: 12 }}><span>Deposit Lines</span></div>
            {items.map((it, i) => {
              const lineAfg = toBase(parseFloat(it.amount) || 0);
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 10, alignItems: 'end', marginBottom: 10 }}>
                  <div className="df-field">
                    <label>{i === 0 ? 'Source Account' : ''}</label>
                    <select required value={it.source_account_id} onChange={e => setItem(i, 'source_account_id', e.target.value)}>
                      <option value="">Select account...</option>
                      {glAccounts.map(a => (
                        <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="df-field">
                    <label>{i === 0 ? 'Description' : ''}</label>
                    <input value={it.description} onChange={e => setItem(i, 'description', e.target.value)} placeholder="e.g. Cash sales" />
                  </div>
                  <div className="df-field">
                    <label>{i === 0 ? `Amount (${currency || baseCurrency})` : ''}</label>
                    <input type="number" step="0.01" min="0.01" required value={it.amount}
                      onChange={e => setItem(i, 'amount', e.target.value)} placeholder="0.00" />
                    {isForeign && parseFloat(it.amount) > 0 && (
                      <div style={{ fontSize: 11, color: '#166534', fontWeight: 600, marginTop: 2 }}>
                        ≈ {fmt(lineAfg)} ({baseCurrency})
                      </div>
                    )}
                  </div>
                  <button type="button" className="btn btn-ghost" title="Remove line"
                    onClick={() => removeItem(i)} disabled={items.length === 1}>✕</button>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border, #e2e8f0)', paddingTop: 12 }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={addItem}>+ Add Line</button>
              <div style={{ textAlign: 'right' }}>
                {isForeign && (
                  <div style={{ fontSize: 12, color: 'var(--desk-text-2)' }}>
                    {fmtCur(totalEntered, currency)} × {fxRate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} = {fmt(total)} ({baseCurrency})
                  </div>
                )}
                <div style={{ fontSize: 16, fontWeight: 700 }}>Total: {fmt(total)}</div>
              </div>
            </div>

            {isForeign && totalEntered > 0 && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13, color: '#166534' }}>
                <strong>{fmtCur(totalEntered, currency)}</strong> = <strong>{fmt(total)}</strong> ({baseCurrency})
                {' '}at 1 {currency} = {fxRate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} {baseCurrency}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}